// src/components/AdminPanel/InterviewManagement.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  collection, onSnapshot, doc, updateDoc, serverTimestamp,
  deleteDoc, addDoc, getDoc, getDocs, query, where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { toast } from "react-toastify";
import { createPortal } from "react-dom";
import emailjs from "emailjs-com";

// All statuses used for badge colouring (includes auto-computed ones)
const STATUS_CONFIG = {
  Scheduled: { bg: "#3182ce", color: "#fff" },
  "Interview Today": { bg: "#d69e2e", color: "#fff" },
  "In Progress": { bg: "#805ad5", color: "#fff" },
  Successful: { bg: "#38a169", color: "#fff" },
  "To Be Continued": { bg: "#dd6b20", color: "#fff" },
  Failed: { bg: "#e53e3e", color: "#fff" },
  "No Show": { bg: "#718096", color: "#fff" },
  "Date Passed": { bg: "#975a16", color: "#fff" },
};

// Only the statuses an admin can manually choose — Date Passed is auto-computed, not selectable
const SELECTABLE_STATUSES = [
  "Scheduled", "Interview Today", "In Progress",
  "Successful", "To Be Continued", "Failed", "No Show",
];

const formatNiceDate = (dateVal) => {
  if (!dateVal) return "N/A";
  let dateObj;
  if (typeof dateVal.toDate === "function") {
    dateObj = dateVal.toDate();
  } else if (typeof dateVal === "string") {
    const parts = dateVal.split("-");
    if (parts.length === 3 && parts[0].length === 2 && parts[2].length === 4) {
      dateObj = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    } else {
      dateObj = new Date(dateVal);
    }
  } else if (dateVal instanceof Date) {
    dateObj = dateVal;
  } else if (typeof dateVal === "number") {
    dateObj = new Date(dateVal);
  }
  if (!dateObj || isNaN(dateObj.getTime())) return typeof dateVal === "string" ? dateVal : "N/A";
  return dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const dropdownBtn = (onClick, color, icon, label, onEnter, onLeave) => (
  <button
    onClick={onClick}
    onMouseEnter={onEnter}
    onMouseLeave={onLeave}
    style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "10px 16px", background: "transparent", border: "none", cursor: "pointer", color, fontSize: "13px", fontWeight: "500", textAlign: "left" }}
  >
    <i className={icon} style={{ width: "14px" }}></i> {label}
  </button>
);

const InterviewManagement = () => {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("All");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const [openStatusSubmenu, setOpenStatusSubmenu] = useState(null);
  const [submenuPos, setSubmenuPos] = useState({ top: 0, right: 0 });

  // Modal states
  const [editModal, setEditModal] = useState({ visible: false, interview: null });
  const [confirmAccept, setConfirmAccept] = useState({ visible: false, interview: null });
  const [confirmReject, setConfirmReject] = useState({ visible: false, interview: null });
  const [confirmBlock, setConfirmBlock] = useState({ visible: false, interview: null });
  const [sendRejectNotify, setSendRejectNotify] = useState(true);
  const [confirmBulkAccept, setConfirmBulkAccept] = useState(false);
  const [confirmBulkReject, setConfirmBulkReject] = useState(false);
  const [confirmBulkBlock, setConfirmBulkBlock] = useState(false);
  const [sendBulkRejectNotify, setSendBulkRejectNotify] = useState(true);
  const [emailLogo, setEmailLogo] = useState("");

  const hasSyncedRef = useRef(false);

  // ── Real-time listener for central interviews collection ─────────────────
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "interviews"), (snap) => {
      setInterviews(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // ── Self-healing backfill & duplicate cleanup ─────────────────────────────
  useEffect(() => {
    if (hasSyncedRef.current) return;
    hasSyncedRef.current = true;

    const autoSyncAndCleanupInterviews = async () => {
      try {
        const [jobSnap, oppSnap, interviewsSnap] = await Promise.all([
          getDocs(collection(db, "jobApplications")),
          getDocs(collection(db, "opportunityApplications")),
          getDocs(collection(db, "interviews")),
        ]);

        // 1. Find and purge any duplicate interview docs already in database
        const candidateDocMap = new Map();
        const duplicateIdsToDelete = [];

        for (const docSnap of interviewsSnap.docs) {
          const data = docSnap.data();
          const key =
            data.candidateId ||
            (data.email && data.sourceType
              ? `${data.email.toLowerCase().trim()}_${data.sourceType}`
              : docSnap.id);

          if (candidateDocMap.has(key)) {
            // Duplicate detected -> mark for deletion
            duplicateIdsToDelete.push(docSnap.id);
          } else {
            candidateDocMap.set(key, docSnap.id);
          }
        }

        if (duplicateIdsToDelete.length > 0) {
          for (const dupId of duplicateIdsToDelete) {
            try {
              await deleteDoc(doc(db, "interviews", dupId));
            } catch (delErr) {
              console.error("[InterviewManagement] Failed to delete duplicate interview:", delErr);
            }
          }
        }

        const isAcceptedOrInterviewing = (status) => {
          const s = (status || "").toLowerCase().trim();
          return s === "accepted" || s === "selected" || s === "interviewing";
        };

        // 2. Backfill missing Job Applications
        for (const docSnap of jobSnap.docs) {
          const app = docSnap.data();
          const key = docSnap.id;
          const emailKey = app.email
            ? `${app.email.toLowerCase().trim()}_job`
            : null;

          if (
            isAcceptedOrInterviewing(app.status) &&
            !candidateDocMap.has(key) &&
            (!emailKey || !candidateDocMap.has(emailKey))
          ) {
            const addedDoc = await addDoc(collection(db, "interviews"), {
              candidateId: docSnap.id,
              sourceType: "job",
              name: app.name || "Unknown",
              email: app.email || "",
              phone: app.phone || "",
              position: app.position || app.jobTitle || "",
              interviewDate: app.interviewDate || "",
              timeSlot: app.interviewSlot || "",
              status: "Scheduled",
              notes: app.notes || "",
              rating: app.rating || 0,
              assignedInterviewer: app.assignedInterviewer || "",
              createdAt: app.createdAt || serverTimestamp(),
              updatedAt: serverTimestamp(),
              reminderSent: false,
              reminderSentAt: null,
            });
            candidateDocMap.set(key, addedDoc.id);
            if (emailKey) candidateDocMap.set(emailKey, addedDoc.id);
          }
        }

        // 3. Backfill missing Opportunity Applications
        for (const docSnap of oppSnap.docs) {
          const app = docSnap.data();
          const key = docSnap.id;
          const emailKey = app.email
            ? `${app.email.toLowerCase().trim()}_reservation`
            : null;

          if (
            isAcceptedOrInterviewing(app.status) &&
            !candidateDocMap.has(key) &&
            (!emailKey || !candidateDocMap.has(emailKey))
          ) {
            const addedDoc = await addDoc(collection(db, "interviews"), {
              candidateId: docSnap.id,
              sourceType: "reservation",
              name: app.name || "Unknown",
              email: app.email || "",
              phone: app.phone || "",
              selectedField: app.selectedField || "",
              opportunityTitle: app.opportunityTitle || "",
              interviewDate: app.interviewDate || "",
              timeSlot: app.interviewSlot || "",
              status: "Scheduled",
              notes: app.notes || "",
              rating: app.rating || 0,
              assignedInterviewer: app.assignedInterviewer || "",
              createdAt: app.createdAt || serverTimestamp(),
              updatedAt: serverTimestamp(),
              reminderSent: false,
              reminderSentAt: null,
            });
            candidateDocMap.set(key, addedDoc.id);
            if (emailKey) candidateDocMap.set(emailKey, addedDoc.id);
          }
        }
      } catch (err) {
        console.error("[InterviewManagement] Auto-sync / cleanup failed:", err);
      }
    };

    autoSyncAndCleanupInterviews();
  }, []);

  useEffect(() => { setSelectedIds(new Set()); }, [activeTab]);

  useEffect(() => {
    const close = () => { setOpenDropdown(null); setOpenStatusSubmenu(null); };
    document.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
    };
  }, []);

  const handleToggleDropdown = (e, id) => {
    e.stopPropagation();
    if (openDropdown === id) {
      setOpenDropdown(null);
      setOpenStatusSubmenu(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
      setOpenDropdown(id);
      setOpenStatusSubmenu(null);
    }
  };

  // ── Client-side deduplication guarantees clean display at all times ───────
  const uniqueInterviews = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const inv of interviews) {
      const key =
        inv.candidateId ||
        (inv.email && inv.sourceType
          ? `${inv.email.toLowerCase().trim()}_${inv.sourceType}`
          : inv.id);
      if (!seen.has(key)) {
        seen.add(key);
        list.push(inv);
      }
    }
    return list;
  }, [interviews]);

  // ── Dynamic effective status: auto-computes "Interview Today" based on date ─
  // Statuses that are terminal (manually set by admin) should never be overridden.
  const TERMINAL_STATUSES = new Set([
    "Successful", "Failed", "No Show", "To Be Continued", "In Progress",
  ]);

  const getEffectiveStatus = (inv) => {
    const stored = inv.status || "Scheduled";

    // Never override a terminal status the admin explicitly set
    if (TERMINAL_STATUSES.has(stored)) return stored;

    const dateVal = inv.interviewDate;
    if (!dateVal) return stored;

    // Parse the interview date (supports DD-MM-YYYY, YYYY-MM-DD, ISO, Timestamp)
    let interviewDate;
    try {
      if (typeof dateVal.toDate === "function") {
        interviewDate = dateVal.toDate();
      } else if (typeof dateVal === "string") {
        const parts = dateVal.split("-");
        if (parts.length === 3 && parts[0].length === 2) {
          // DD-MM-YYYY
          interviewDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        } else {
          interviewDate = new Date(dateVal);
        }
      } else if (dateVal instanceof Date) {
        interviewDate = dateVal;
      } else if (typeof dateVal === "number") {
        interviewDate = new Date(dateVal);
      }
    } catch {
      return stored;
    }

    if (!interviewDate || isNaN(interviewDate.getTime())) return stored;

    const today = new Date();
    const isToday =
      interviewDate.getFullYear() === today.getFullYear() &&
      interviewDate.getMonth() === today.getMonth() &&
      interviewDate.getDate() === today.getDate();

    if (isToday) return "Interview Today";

    // Interview date is in the past and admin hasn't set a terminal status yet
    const isPast =
      interviewDate.getFullYear() < today.getFullYear() ||
      (interviewDate.getFullYear() === today.getFullYear() &&
        interviewDate.getMonth() < today.getMonth()) ||
      (interviewDate.getFullYear() === today.getFullYear() &&
        interviewDate.getMonth() === today.getMonth() &&
        interviewDate.getDate() < today.getDate());

    if (isPast) return "Date Passed";

    return stored;
  };

  // Enrich uniqueInterviews with effective (dynamic) status for rendering
  const enrichedInterviews = useMemo(
    () => uniqueInterviews.map((inv) => ({ ...inv, effectiveStatus: getEffectiveStatus(inv) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uniqueInterviews]
  );

  const currentInterviews = useMemo(() => {
    let filtered = enrichedInterviews;
    if (activeTab === "Job") filtered = enrichedInterviews.filter(inv => inv.sourceType === "job");
    else if (activeTab === "Reservation") filtered = enrichedInterviews.filter(inv => inv.sourceType === "reservation");
    return filtered.sort((a, b) => {
      const timeA = a.createdAt?.toDate?.()?.getTime() || 0;
      const timeB = b.createdAt?.toDate?.()?.getTime() || 0;
      return timeB - timeA;
    });
  }, [enrichedInterviews, activeTab]);

  const stats = useMemo(() => ({
    total: enrichedInterviews.length,
    today: enrichedInterviews.filter(i => i.effectiveStatus === "Interview Today").length,
    scheduled: enrichedInterviews.filter(i => i.effectiveStatus === "Scheduled").length,
    datePassed: enrichedInterviews.filter(i => i.effectiveStatus === "Date Passed").length,
    successful: enrichedInterviews.filter(i => i.effectiveStatus === "Successful").length,
    tbc: enrichedInterviews.filter(i => i.effectiveStatus === "To Be Continued").length,
    noShow: enrichedInterviews.filter(i => i.effectiveStatus === "No Show").length,
    failed: enrichedInterviews.filter(i => i.effectiveStatus === "Failed").length,
  }), [enrichedInterviews]);

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedIds(new Set(currentInterviews.map(inv => inv.id)));
    else setSelectedIds(new Set());
  };

  const handleSelectRow = (e, id) => {
    const s = new Set(selectedIds);
    if (e.target.checked) s.add(id); else s.delete(id);
    setSelectedIds(s);
  };

  const handleStatusUpdate = async (id, newStatus) => {
    setOpenDropdown(null);
    setOpenStatusSubmenu(null);
    try {
      await updateDoc(doc(db, "interviews", id), { status: newStatus, updatedAt: serverTimestamp() });
      toast.success(`Status updated to ${newStatus}`);
    } catch (err) {
      toast.error("Failed to update status");
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "interviews", id));
      toast.success("Interview deleted");
    } catch (err) {
      toast.error("Failed to delete interview");
      console.error(err);
    }
  };

  // Accept — creates hired/onboarded profile card, deletes interview, marks original app as hired/onboarded
  useEffect(() => {
    emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
    const fetchLogo = async () => {
      try {
        const snap = await getDoc(doc(db, "logos", "xrSCDlcVzp10becG8bnW"));
        if (snap.exists()) setEmailLogo(snap.data().emailLogo || "");
      } catch { }
    };
    fetchLogo();
  }, []);

  const handleSendRejectionEmail = async (inv) => {
    const year = new Date().getFullYear();
    const logoUrl = emailLogo || "https://i.postimg.cc/023xvzzm/vbt-logo-2.png";
    const roleLabel = inv.sourceType === "job"
      ? (inv.position || "N/A").replace(/&#x2F;/gi, "/")
      : [inv.opportunityTitle, inv.selectedField].filter(Boolean).join(" — ") || "N/A";
    const htmlBody =
      "<table width='100%' cellpadding='0' cellspacing='0' style='background:#f0f4f8;padding:36px 0;font-family:Segoe UI,Arial,sans-serif;'>" +
      "<tr><td align='center'>" +
      "<table width='600' cellpadding='0' cellspacing='0' style='background:#ffffff;border-radius:16px;overflow:hidden;'>" +
      "<tr><td style='background:#ff8000;text-align:center;padding:36px 24px 30px;'>" +
      "<img src='" + logoUrl + "' width='160px' alt='VBT' style='display:block;margin:0 auto 18px;' />" +
      "<h1 style='margin:0;color:#ffffff;font-size:24px;font-weight:700;'>Application Update</h1>" +
      "</td></tr>" +
      "<tr><td style='padding:32px 32px 24px;'>" +
      "<p style='margin:0 0 16px;font-size:16px;color:#222;line-height:1.7;'>Dear <strong style='color:#ff6600;'>" + inv.name + "</strong>,</p>" +
      "<p style='margin:0 0 16px;font-size:14px;color:#555;line-height:1.8;'>Thank you for taking the time to apply for the <strong>" + roleLabel + "</strong> opportunity at VisionBird Technologies. We truly appreciate your interest in joining our team.</p>" +
      "<p style='margin:0 0 16px;font-size:14px;color:#555;line-height:1.8;'>We received a wonderful response for this role, and unfortunately, we have already filled all our available seats with other candidates. Please know that our decision was simply a matter of limited capacity rather than a reflection of your talent or potential.</p>" +
      "<p style='margin:0 0 16px;font-size:14px;color:#555;line-height:1.8;'>We encourage you to keep developing your skills and to apply again in the future when new opportunities open up. We are very grateful for your effort and wish you wonderful success in your career journey.</p>" +
      "</td></tr>" +
      "<tr><td style='padding:0 32px 28px;'>" +
      "<p style='margin:0;font-size:14px;color:#555;line-height:1.8;'>We wish you the best in your journey.</p>" +
      "<p style='margin:14px 0 0;font-size:14px;color:#333;'>Warm Regards,<br /><strong>VisionBird Technologies &mdash; HR Team</strong></p>" +
      "</td></tr>" +
      "<tr><td style='background:#111111;text-align:center;padding:22px;'>" +
      "<p style='margin:0 0 4px;color:#ff8000;font-size:13px;font-weight:700;letter-spacing:0.5px;'>VISIONBIRD TECHNOLOGIES</p>" +
      "<p style='margin:0;color:#666;font-size:12px;'>&#169; " + year + " &nbsp;&#183;&nbsp; Please do not reply to this email</p>" +
      "</td></tr></table></td></tr></table>";
    const cleanEmail = (inv.email || "").trim();
    const params = {
      to_email: cleanEmail,
      email: cleanEmail,
      subject: "Your Application for " + roleLabel + " | VisionBird Technologies",
      message: htmlBody,
    };
    console.log("[InterviewManagement] Sending rejection email:", {
      to: params.to_email,
      subject: params.subject,
      service: process.env.REACT_APP_EMAILJS_SERVICE_ID,
      template: process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
    });
    const result = await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      params,
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY
    );
    console.log("[InterviewManagement] Rejection EmailJS response:", result);
  };
  const handleSendReminderEmail = async (inv) => {
    const year = new Date().getFullYear();
    const logoUrl = emailLogo || "https://i.postimg.cc/023xvzzm/vbt-logo-2.png";
    const roleLabel = inv.sourceType === "job"
      ? (inv.position || "N/A").replace(/&#x2F;/gi, "/")
      : [inv.opportunityTitle, inv.selectedField].filter(Boolean).join(" — ") || "N/A";
    const interviewDate = formatNiceDate(inv.interviewDate);
    const timeSlot = inv.timeSlot || "To be confirmed";
    const interviewer = inv.assignedInterviewer || "Our HR Team";
    const htmlBody =
      "<table width='100%' cellpadding='0' cellspacing='0' style='background:#f0f4f8;padding:36px 0;font-family:Segoe UI,Arial,sans-serif;'>" +
      "<tr><td align='center'>" +
      "<table width='600' cellpadding='0' cellspacing='0' style='background:#ffffff;border-radius:16px;overflow:hidden;'>" +
      "<tr><td style='background:#ff8000;text-align:center;padding:36px 24px 30px;'>" +
      "<img src='" + logoUrl + "' width='160px' alt='VBT' style='display:block;margin:0 auto 18px;' />" +
      "<h1 style='margin:0;color:#ffffff;font-size:24px;font-weight:700;'>We Missed You Today</h1>" +
      "</td></tr>" +
      "<tr><td style='padding:32px 32px 20px;'>" +
      "<p style='margin:0 0 16px;font-size:16px;color:#222;line-height:1.7;'>Dear <strong style='color:#ff6600;'>" + inv.name + "</strong>,</p>" +
      "<p style='margin:0 0 16px;font-size:14px;color:#555;line-height:1.8;'>We hope you are doing well. We noticed that you were unable to attend your scheduled interview with <strong>VisionBird Technologies</strong> and we completely understand that unexpected situations can arise at any time.</p>" +
      "<div style='background:#fff8f0;border-left:4px solid #ff8000;border-radius:8px;padding:18px 22px;margin:20px 0;'>" +
      "<p style='margin:0 0 10px;font-size:13px;font-weight:700;color:#c05621;text-transform:uppercase;letter-spacing:0.5px;'>Scheduled Interview Details</p>" +
      "<table cellpadding='0' cellspacing='0' style='width:100%;'>" +
      "<tr><td style='padding:5px 0;font-size:14px;color:#4a5568;width:130px;'>Role / Opportunity</td><td style='padding:5px 0;font-size:14px;color:#1a202c;font-weight:600;'>" + roleLabel + "</td></tr>" +
      "<tr><td style='padding:5px 0;font-size:14px;color:#4a5568;'>Date</td><td style='padding:5px 0;font-size:14px;color:#1a202c;font-weight:600;'>" + interviewDate + "</td></tr>" +
      "<tr><td style='padding:5px 0;font-size:14px;color:#4a5568;'>Time</td><td style='padding:5px 0;font-size:14px;color:#1a202c;font-weight:600;'>" + timeSlot + "</td></tr>" +
      "<tr><td style='padding:5px 0;font-size:14px;color:#4a5568;'>Interviewer</td><td style='padding:5px 0;font-size:14px;color:#1a202c;font-weight:600;'>" + interviewer + "</td></tr>" +
      "</table></div>" +
      "<p style='margin:0 0 16px;font-size:14px;color:#555;line-height:1.8;'>We still value your interest in joining our team and would genuinely love the opportunity to connect with you. If you would like to reschedule your interview or share what happened, please do not hesitate to reach out to us, we are here and happy to accommodate you.</p>" +
      "<p style='margin:0 0 16px;font-size:14px;color:#555;line-height:1.8;'>We sincerely hope everything is alright on your end and look forward to hearing from you soon.</p>" +
      "</td></tr>" +
      "<tr><td style='padding:0 32px 28px;'>" +
      "<p style='margin:0;font-size:14px;color:#555;line-height:1.8;'>Take care and stay well!</p>" +
      "<p style='margin:14px 0 0;font-size:14px;color:#333;'>Warm Regards,<br /><strong>VisionBird Technologies &mdash; HR Team</strong></p>" +
      "</td></tr>" +
      "<tr><td style='background:#111111;text-align:center;padding:22px;'>" +
      "<p style='margin:0 0 4px;color:#ff8000;font-size:13px;font-weight:700;letter-spacing:0.5px;'>VISIONBIRD TECHNOLOGIES</p>" +
      "<p style='margin:0;color:#666;font-size:12px;'>&#169; " + year + " &nbsp;&#183;&nbsp; Please do not reply to this email</p>" +
      "</td></tr></table></td></tr></table>";
    const cleanEmail = (inv.email || "").trim();
    const params = {
      to_email: cleanEmail,
      email: cleanEmail,
      subject: "We Missed You — " + roleLabel + " Interview | VisionBird Technologies",
      message: htmlBody,
    };
    const result = await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      params,
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY
    );
    console.log("[InterviewManagement] Reminder EmailJS response:", result);
  };

  const handleSendReminderIndividual = async (inv) => {
    if (!inv.email) {
      toast.warning("No email address found for this candidate.");
      return;
    }
    try {
      await handleSendReminderEmail(inv);
      await updateDoc(doc(db, "interviews", inv.id), { reminderSent: true, reminderSentAt: serverTimestamp() });
      toast.success("Reminder sent to " + inv.email);
    } catch (err) {
      console.error("[InterviewManagement] Reminder email failed:", err);
      toast.error("Failed to send reminder: " + (err?.text || err?.message || JSON.stringify(err)));
    }
  };

  const handleConfirmAccept = async () => {
    const inv = confirmAccept.interview;
    setConfirmAccept({ visible: false, interview: null });
    try {
      if (inv.sourceType === "job") {
        let position = inv.position || "";
        if (!position && inv.candidateId) {
          try {
            const snap = await getDoc(doc(db, "jobApplications", inv.candidateId));
            if (snap.exists()) position = snap.data().position || snap.data().jobTitle || "";
          } catch { }
        }
        await addDoc(collection(db, "hired"), {
          interviewId: inv.id,
          candidateId: inv.candidateId || "",
          sourceType: "job",
          name: inv.name || "",
          email: inv.email || "",
          phone: inv.phone || "",
          position,
          department: "",
          startDate: "",
          salary: "",
          status: "Active",
          notes: inv.notes || "",
          interviewRating: Number(inv.rating) || 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        if (inv.candidateId) {
          try {
            await updateDoc(doc(db, "jobApplications", inv.candidateId), {
              status: "hired",
              statusUpdatedAt: serverTimestamp(),
            });
          } catch { }
        }
        toast.success(`${inv.name} accepted and added to Hired Employees!`);
      } else {
        let selectedField = inv.selectedField || "";
        let opportunityTitle = inv.opportunityTitle || "";
        if (!selectedField && inv.candidateId) {
          try {
            const snap = await getDoc(doc(db, "opportunityApplications", inv.candidateId));
            if (snap.exists()) {
              selectedField = snap.data().selectedField || "";
              opportunityTitle = snap.data().opportunityTitle || "";
            }
          } catch { }
        }
        await addDoc(collection(db, "onboarded"), {
          interviewId: inv.id,
          candidateId: inv.candidateId || "",
          sourceType: "reservation",
          name: inv.name || "",
          email: inv.email || "",
          phone: inv.phone || "",
          selectedField,
          opportunityTitle,
          startDate: "",
          endDate: "",
          status: "Active",
          notes: inv.notes || "",
          interviewRating: Number(inv.rating) || 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        if (inv.candidateId) {
          try {
            await updateDoc(doc(db, "opportunityApplications", inv.candidateId), {
              status: "onboarded",
              statusUpdatedAt: serverTimestamp(),
            });
          } catch { }
        }
        toast.success(`${inv.name} accepted and added to Onboarded Trainees!`);
      }
      await deleteDoc(doc(db, "interviews", inv.id));
    } catch (err) {
      toast.error("Failed to accept interview");
      console.error(err);
    }
  };

  // Reject — restores original application to rejected, deletes interview
  const handleConfirmReject = async () => {
    const inv = confirmReject.interview;
    if (!inv) return;
    setConfirmReject({ visible: false, interview: null });
    try {
      const collection_name = inv.sourceType === "job" ? "jobApplications" : "opportunityApplications";
      if (inv.candidateId) {
        await updateDoc(doc(db, collection_name, inv.candidateId), {
          status: "rejected",
          statusUpdatedAt: serverTimestamp(),
        });
      }
      await deleteDoc(doc(db, "interviews", inv.id));
      toast.success(`${inv.name} rejected and moved back to applications.`);

      if (sendRejectNotify) {
        if (!inv.email) {
          toast.warning("Status updated, but no email address was found to notify the candidate.");
          return;
        }
        await handleSendRejectionEmail(inv);
        toast.info("Rejection notification sent to " + inv.email + ".");
      }
    } catch (err) {
      console.error("[InterviewManagement] Reject/email failed:", err);
      toast.error("Email failed: " + (err?.text || err?.message || JSON.stringify(err)));
    }
  };

  // Block — restores original application to blocked, deletes interview
  const handleConfirmBlock = async () => {
    const inv = confirmBlock.interview;
    setConfirmBlock({ visible: false, interview: null });
    try {
      const collection_name = inv.sourceType === "job" ? "jobApplications" : "opportunityApplications";
      if (inv.candidateId) {
        await updateDoc(doc(db, collection_name, inv.candidateId), {
          status: "blocked",
          statusUpdatedAt: serverTimestamp(),
        });
      }
      await deleteDoc(doc(db, "interviews", inv.id));
      toast.success(`${inv.name} blocked and removed from interviews.`);
    } catch (err) {
      toast.error("Failed to block");
      console.error(err);
    }
  };

  const handleSaveDetails = async (e) => {
    e.preventDefault();
    const id = editModal.interview.id;
    const inv = editModal.interview;
    try {
      await updateDoc(doc(db, "interviews", id), {
        interviewDate: inv.interviewDate,
        timeSlot: inv.timeSlot,
        assignedInterviewer: inv.assignedInterviewer,
        notes: inv.notes,
        rating: inv.rating,
        updatedAt: serverTimestamp(),
      });
      if (inv.candidateId) {
        const col = inv.sourceType === "job" ? "jobApplications" : "opportunityApplications";
        try {
          await updateDoc(doc(db, col, inv.candidateId), {
            interviewDate: inv.interviewDate,
            interviewSlot: inv.timeSlot,
            statusUpdatedAt: serverTimestamp(),
          });
        } catch { }
      }
      toast.success("Interview details updated");
      setEditModal({ visible: false, interview: null });
    } catch (err) {
      toast.error("Failed to update details");
      console.error(err);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    try {
      if (action === "Send Reminder") {
        const selectedInvs = currentInterviews.filter(inv => selectedIds.has(inv.id));
        let sentCount = 0;
        for (const inv of selectedInvs) {
          await updateDoc(doc(db, "interviews", inv.id), { reminderSent: true, reminderSentAt: serverTimestamp() });
          if (inv.email) {
            try { await handleSendReminderEmail(inv); sentCount++; } catch (err) {
              console.error("[InterviewManagement] Bulk reminder email failed for", inv.email, err);
            }
          }
        }
        toast.success(`Reminder${sentCount !== 1 ? "s" : ""} sent to ${sentCount} candidate${sentCount !== 1 ? "s" : ""}`);
      } else if (action === "Mark Interview Today") {
        for (const id of selectedIds) {
          await updateDoc(doc(db, "interviews", id), { status: "Interview Today", updatedAt: serverTimestamp() });
        }
        toast.success("Status updated to Interview Today");
      } else if (action === "Export Selected") {
        const selectedData = currentInterviews.filter(inv => selectedIds.has(inv.id));
        const csvContent = "data:text/csv;charset=utf-8," +
          ["Candidate Name,Source,Date,Time,Interviewer,Status,Rating,Notes"]
            .concat(selectedData.map(row =>
              `${row.name},${row.sourceType},${formatNiceDate(row.interviewDate)},${row.timeSlot},${row.assignedInterviewer},${row.status},${row.rating},"${(row.notes || "").replace(/"/g, '""')}"`
            )).join("\n");
        const link = document.createElement("a");
        link.setAttribute("href", encodeURI(csvContent));
        link.setAttribute("download", "interviews_export.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setSelectedIds(new Set());
    } catch (err) {
      toast.error("Bulk action failed");
      console.error(err);
    }
  };

  const handleConfirmBulkAccept = async () => {
    setConfirmBulkAccept(false);
    const selectedInvs = currentInterviews.filter(inv => selectedIds.has(inv.id));
    if (selectedInvs.length === 0) return;
    let successCount = 0;
    try {
      for (const inv of selectedInvs) {
        if (inv.sourceType === "job") {
          let position = inv.position || "";
          if (!position && inv.candidateId) {
            try {
              const snap = await getDoc(doc(db, "jobApplications", inv.candidateId));
              if (snap.exists()) position = snap.data().position || snap.data().jobTitle || "";
            } catch { }
          }
          await addDoc(collection(db, "hired"), {
            interviewId: inv.id, candidateId: inv.candidateId || "", sourceType: "job",
            name: inv.name || "", email: inv.email || "", phone: inv.phone || "",
            position, department: "", startDate: "", salary: "", status: "Active",
            notes: inv.notes || "", interviewRating: Number(inv.rating) || 0,
            createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
          });
          if (inv.candidateId) {
            await updateDoc(doc(db, "jobApplications", inv.candidateId), { status: "hired", statusUpdatedAt: serverTimestamp() });
          }
        } else {
          let selectedField = inv.selectedField || "";
          let opportunityTitle = inv.opportunityTitle || "";
          if (!selectedField && inv.candidateId) {
            try {
              const snap = await getDoc(doc(db, "opportunityApplications", inv.candidateId));
              if (snap.exists()) { selectedField = snap.data().selectedField || ""; opportunityTitle = snap.data().opportunityTitle || ""; }
            } catch { }
          }
          await addDoc(collection(db, "onboarded"), {
            interviewId: inv.id, candidateId: inv.candidateId || "", sourceType: "reservation",
            name: inv.name || "", email: inv.email || "", phone: inv.phone || "",
            selectedField, opportunityTitle, startDate: "", endDate: "", status: "Active",
            notes: inv.notes || "", interviewRating: Number(inv.rating) || 0,
            createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
          });
          if (inv.candidateId) {
            await updateDoc(doc(db, "opportunityApplications", inv.candidateId), { status: "onboarded", statusUpdatedAt: serverTimestamp() });
          }
        }
        await deleteDoc(doc(db, "interviews", inv.id));
        successCount++;
      }
      toast.success(`Successfully accepted ${successCount} candidate${successCount !== 1 ? "s" : ""}!`);
      setSelectedIds(new Set());
    } catch (err) {
      toast.error("Bulk accept failed");
      console.error(err);
    }
  };

  const handleConfirmBulkReject = async () => {
    setConfirmBulkReject(false);
    const selectedInvs = currentInterviews.filter(inv => selectedIds.has(inv.id));
    if (selectedInvs.length === 0) return;
    let successCount = 0;
    try {
      for (const inv of selectedInvs) {
        const col = inv.sourceType === "job" ? "jobApplications" : "opportunityApplications";
        if (inv.candidateId) {
          await updateDoc(doc(db, col, inv.candidateId), { status: "rejected", statusUpdatedAt: serverTimestamp() });
        }
        await deleteDoc(doc(db, "interviews", inv.id));
        successCount++;
      }
      toast.success(`Successfully rejected ${successCount} candidate${successCount !== 1 ? "s" : ""}!`);
      if (sendBulkRejectNotify) {
        let emailsSent = 0;
        for (const inv of selectedInvs) {
          if (inv.email) {
            try { await handleSendRejectionEmail(inv); emailsSent++; } catch (err) {
              console.error("[InterviewManagement] Bulk rejection email failed for", inv.email, err);
            }
          }
        }
        if (emailsSent > 0) toast.info(`Rejection notifications sent to ${emailsSent} candidate${emailsSent !== 1 ? "s" : ""}.`);
      }
      setSelectedIds(new Set());
    } catch (err) {
      toast.error("Bulk reject failed: " + (err?.message || JSON.stringify(err)));
      console.error(err);
    }
  };

  const handleConfirmBulkBlock = async () => {
    setConfirmBulkBlock(false);
    const selectedInvs = currentInterviews.filter(inv => selectedIds.has(inv.id));
    if (selectedInvs.length === 0) return;
    let successCount = 0;
    try {
      for (const inv of selectedInvs) {
        const col = inv.sourceType === "job" ? "jobApplications" : "opportunityApplications";
        if (inv.candidateId) {
          await updateDoc(doc(db, col, inv.candidateId), { status: "blocked", statusUpdatedAt: serverTimestamp() });
        }
        await deleteDoc(doc(db, "interviews", inv.id));
        successCount++;
      }
      toast.success(`Successfully blocked ${successCount} candidate${successCount !== 1 ? "s" : ""}!`);
      setSelectedIds(new Set());
    } catch (err) {
      toast.error("Bulk block failed");
      console.error(err);
    }
  };

  const cardGradient = "linear-gradient(135deg, #FF8C00 0%, #E67E00 100%)";
  const highlightedGradient = "linear-gradient(135deg, #FF9F2E 0%, #FF8C00 100%)";

  if (loading) return <div style={{ padding: "40px", textAlign: "center", color: "#718096" }}>Loading interviews...</div>;

  return (
    <div className="dashboard-page">
      <h1 className="page-heading">
        <i className="fas fa-users-cog" style={{ color: "#FF8C00" }}></i> Interview Management
      </h1>

      {/* Stats Cards */}
      <div style={{ display: "flex", gap: "15px", marginBottom: "30px", flexWrap: "wrap" }}>
        {[
          { label: "Total Interviews", count: stats.total, icon: "fas fa-list", bg: highlightedGradient },
          { label: "Today's Interviews", count: stats.today, icon: "fas fa-calendar-day", bg: cardGradient },
          { label: "Scheduled", count: stats.scheduled, icon: "fas fa-calendar-alt", bg: cardGradient },
          { label: "Date Passed", count: stats.datePassed, icon: "fas fa-calendar-times", bg: "linear-gradient(135deg, #744210 0%, #975a16 100%)" },
          { label: "Successful", count: stats.successful, icon: "fas fa-check-circle", bg: cardGradient },
          { label: "To Be Continued", count: stats.tbc, icon: "fas fa-forward", bg: cardGradient },
          { label: "No Show", count: stats.noShow, icon: "fas fa-user-slash", bg: cardGradient },
          { label: "Failed", count: stats.failed, icon: "fas fa-times-circle", bg: cardGradient },
        ].map((stat, idx) => (
          <div key={idx} style={{ background: stat.bg, padding: "16px", borderRadius: "12px", color: "white", flex: 1, minWidth: "120px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", border: "1px solid rgba(255,255,255,0.15)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
              <i className={stat.icon} style={{ fontSize: "20px", opacity: 0.9 }}></i>
              <h3 style={{ margin: 0, fontSize: "12px", fontWeight: "500", opacity: 0.9 }}>{stat.label}</h3>
            </div>
            <p style={{ margin: 0, fontSize: "28px", fontWeight: "bold" }}>{stat.count}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="category-tabs" style={{ display: "flex", gap: "0", borderBottom: "1px solid #E2E8F0", marginBottom: "20px" }}>
        {["All", "Job", "Reservation"].map(tab => (
          <button
            key={tab}
            className={activeTab === tab ? "tab active" : "tab"}
            onClick={() => setActiveTab(tab)}
            style={{ padding: "12px 24px", background: activeTab === tab ? "#FF8C00" : "transparent", color: activeTab === tab ? "white" : "#4A5568", border: "none", borderRadius: "8px 8px 0 0", cursor: "pointer", fontSize: "14px", fontWeight: "600", transition: "all 0.2s", marginRight: "4px" }}
          >
            {tab} Interviews
          </button>
        ))}
      </div>

      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", marginBottom: "15px", alignItems: "center", background: "#f7fafc", padding: "10px 14px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
          <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontWeight: "600", fontSize: "14px", color: "#4a5568" }}>{selectedIds.size} selected</span>
            <button onClick={() => handleBulkAction("Send Reminder")} style={{ padding: "6px 12px", background: "#3182ce", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}>Send Reminder</button>
            <button onClick={() => handleBulkAction("Mark Interview Today")} style={{ padding: "6px 12px", background: "#d69e2e", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}>Mark Interview Today</button>
            <button onClick={() => handleBulkAction("Export Selected")} style={{ padding: "6px 12px", background: "#4a5568", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}>Export Selected</button>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <div style={{ width: "1px", height: "24px", background: "#e2e8f0", marginRight: "2px" }}></div>
            <button
              onClick={() => setConfirmBulkAccept(true)}
              title="Bulk Accept"
              style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 14px", background: "#38a169", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}
            >
              <i className="fas fa-check-circle"></i> Accept
            </button>
            <button
              onClick={() => setConfirmBulkReject(true)}
              title="Bulk Reject"
              style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 14px", background: "#e53e3e", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}
            >
              <i className="fas fa-times-circle"></i> Reject
            </button>
            <button
              onClick={() => setConfirmBulkBlock(true)}
              title="Bulk Block"
              style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 14px", background: "#718096", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}
            >
              <i className="fas fa-ban"></i> Block
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="candidate-table-container">
        <div style={{ overflowX: "auto", minHeight: "400px", paddingBottom: "180px" }}>
          <table style={{ width: "100%" }}>
            <thead>
              <tr>
                <th><input type="checkbox" className="oval-checkbox" checked={currentInterviews.length > 0 && selectedIds.size === currentInterviews.length} onChange={handleSelectAll} /></th>
                <th>Name</th>
                <th>Type</th>
                <th>Role / Field</th>
                <th>Date &amp; Time</th>
                <th>Interviewer</th>
                <th>Overall Rating</th>
                <th>Interview Notes</th>
                <th>Status</th>
                <th>Reminder</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentInterviews.length > 0 ? currentInterviews.map((inv) => (
                <tr key={inv.id}>
                  <td><input type="checkbox" className="oval-checkbox" checked={selectedIds.has(inv.id)} onChange={(e) => handleSelectRow(e, inv.id)} /></td>
                  <td>
                    <div><strong>{inv.name}</strong></div>
                    <div style={{ fontSize: "12px", color: "#718096" }}>{inv.email}</div>
                    <div style={{ fontSize: "12px", color: "#718096" }}>{inv.phone}</div>
                  </td>
                  <td>
                    <span style={{ background: inv.sourceType === "job" ? "#ebf4ff" : "#f0fff4", color: inv.sourceType === "job" ? "#2b6cb0" : "#2f855a", padding: "4px 8px", borderRadius: "12px", fontSize: "12px", textTransform: "capitalize", fontWeight: "600" }}>
                      {inv.sourceType}
                    </span>
                  </td>
                  <td>
                    {inv.sourceType === "job"
                      ? <span style={{ background: "#fff8f0", color: "#c05621", padding: "4px 10px", borderRadius: "10px", fontSize: "12px", fontWeight: "600", display: "inline-block" }}>{inv.position || "—"}</span>
                      : <span style={{ background: "#f0fff4", color: "#276749", padding: "4px 10px", borderRadius: "10px", fontSize: "12px", fontWeight: "600", display: "inline-block" }}>{[inv.opportunityTitle, inv.selectedField].filter(Boolean).join(" — ") || "—"}</span>
                    }
                  </td>
                  <td>
                    <div>{formatNiceDate(inv.interviewDate)}</div>
                    <div style={{ fontSize: "12px", color: "#718096" }}>{inv.timeSlot || "—"}</div>
                  </td>
                  <td>{inv.assignedInterviewer || "—"}</td>
                  <td>
                    {inv.rating !== undefined && inv.rating !== null && inv.rating !== "" ? (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: "4px",
                        background: Number(inv.rating) >= 7 ? "#f0fff4" : Number(inv.rating) >= 4 ? "#fffaf0" : "#fff5f5",
                        color: Number(inv.rating) >= 7 ? "#22543d" : Number(inv.rating) >= 4 ? "#c05621" : "#c53030",
                        border: `1px solid ${Number(inv.rating) >= 7 ? "#9ae6b4" : Number(inv.rating) >= 4 ? "#fbd38d" : "#feb2b2"}`,
                        padding: "4px 8px", borderRadius: "12px", fontSize: "12px", fontWeight: "700"
                      }}>
                        <i className="fas fa-star" style={{ color: "#ECC94B", fontSize: "11px" }}></i>
                        {inv.rating}/10
                      </span>
                    ) : (
                      <span style={{ color: "#a0aec0", fontSize: "12px" }}>—</span>
                    )}
                  </td>
                  <td style={{ maxWidth: "220px", minWidth: "150px" }}>
                    {inv.notes ? (
                      <div style={{
                        fontSize: "12px", color: "#2d3748", background: "#f8fafc",
                        border: "1px solid #e2e8f0", borderRadius: "8px", padding: "6px 10px",
                        whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "65px", overflowY: "auto",
                        lineHeight: "1.4"
                      }}>
                        <i className="fas fa-sticky-note" style={{ color: "#ff8000", marginRight: "5px" }}></i>
                        {inv.notes}
                      </div>
                    ) : (
                      <span style={{ color: "#a0aec0", fontSize: "12px", fontStyle: "italic" }}>No notes</span>
                    )}
                  </td>
                  <td>
                    <span style={{ backgroundColor: STATUS_CONFIG[inv.effectiveStatus]?.bg || "#718096", color: STATUS_CONFIG[inv.effectiveStatus]?.color || "#fff", padding: "4px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: "600" }}>
                      {inv.effectiveStatus}
                    </span>
                  </td>
                  <td>
                    {inv.reminderSent
                      ? <span style={{ fontSize: "12px", color: "#38a169", fontWeight: "600" }}><i className="fas fa-check"></i> Sent</span>
                      : <span style={{ fontSize: "12px", color: "#a0aec0" }}>Not Sent</span>
                    }
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <button
                      className="action-button"
                      onClick={(e) => handleToggleDropdown(e, inv.id)}
                      style={{ background: "transparent", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "6px 12px", cursor: "pointer", fontSize: "20px", fontWeight: "bold", color: "#4A5568" }}
                    >
                      &#8942;
                    </button>

                    {openDropdown === inv.id && createPortal(
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ position: "fixed", top: `${dropdownPos.top}px`, right: `${dropdownPos.right}px`, background: "#ffffff", border: "1px solid #E2E8F0", borderRadius: "12px", boxShadow: "0 8px 24px rgba(0,0,0,0.2)", zIndex: 999999, minWidth: "190px", overflow: "hidden" }}
                      >
                        {/* Edit Details */}
                        <button
                          onClick={() => { setEditModal({ visible: true, interview: { ...inv } }); setOpenDropdown(null); }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#f7f8fa")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                          style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "10px 16px", background: "transparent", border: "none", cursor: "pointer", color: "#2d3748", fontSize: "13px", fontWeight: "500", textAlign: "left" }}
                        >
                          <i className="fas fa-edit" style={{ width: "14px" }}></i> Edit Details
                        </button>

                        {/* Update Status — click opens a second portal dropdown */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            setSubmenuPos({ top: rect.top, right: window.innerWidth - rect.left + 4 });
                            setOpenStatusSubmenu(openStatusSubmenu === inv.id ? null : inv.id);
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#f7f8fa")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                          style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: openStatusSubmenu === inv.id ? "#f0f4ff" : "transparent", border: "none", cursor: "pointer", color: openStatusSubmenu === inv.id ? "#3182ce" : "#2d3748", fontSize: "13px", fontWeight: "500", textAlign: "left" }}
                        >
                          <span><i className="fas fa-sync-alt" style={{ width: "14px", marginRight: "10px" }}></i>Update Status</span>
                          <i className="fas fa-chevron-left" style={{ fontSize: "10px", opacity: 0.5 }}></i>
                        </button>

                        {/* Send Reminder */}
                        <button
                          onClick={() => { handleSendReminderIndividual(inv); setOpenDropdown(null); }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#ebf8ff")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                          style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "10px 16px", background: "transparent", border: "none", cursor: "pointer", color: "#3182ce", fontSize: "13px", fontWeight: "500", textAlign: "left" }}
                        >
                          <i className="fas fa-bell" style={{ width: "14px" }}></i> Send Reminder
                        </button>

                        <div style={{ borderTop: "1px solid #e2e8f0", margin: "4px 0" }}></div>

                        {/* Accept */}
                        <button
                          onClick={() => { setConfirmAccept({ visible: true, interview: inv }); setOpenDropdown(null); }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#f0fff4")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                          style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "10px 16px", background: "transparent", border: "none", cursor: "pointer", color: "#38a169", fontSize: "13px", fontWeight: "600", textAlign: "left" }}
                        >
                          <i className="fas fa-check-circle" style={{ width: "14px" }}></i> Accept
                        </button>

                        {/* Reject */}
                        <button
                          onClick={() => { setConfirmReject({ visible: true, interview: inv }); setOpenDropdown(null); }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#fff5f5")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                          style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "10px 16px", background: "transparent", border: "none", cursor: "pointer", color: "#e53e3e", fontSize: "13px", fontWeight: "500", textAlign: "left" }}
                        >
                          <i className="fas fa-times-circle" style={{ width: "14px" }}></i> Reject
                        </button>

                        {/* Block */}
                        <button
                          onClick={() => { setConfirmBlock({ visible: true, interview: inv }); setOpenDropdown(null); }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#f7f8fa")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                          style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "10px 16px", background: "transparent", border: "none", cursor: "pointer", color: "#718096", fontSize: "13px", fontWeight: "500", textAlign: "left" }}
                        >
                          <i className="fas fa-ban" style={{ width: "14px" }}></i> Block
                        </button>

                        <div style={{ borderTop: "1px solid #e2e8f0", margin: "4px 0" }}></div>

                        {/* Delete */}
                        <button
                          onClick={() => { handleDelete(inv.id); setOpenDropdown(null); }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#fff5f5")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                          style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "10px 16px", background: "transparent", border: "none", cursor: "pointer", color: "#e53e3e", fontSize: "13px", fontWeight: "500", textAlign: "left" }}
                        >
                          <i className="fas fa-trash" style={{ width: "14px" }}></i> Delete
                        </button>
                      </div>,
                      document.body
                    )}

                    {/* Status submenu — second portal, appears to the left of the main dropdown */}
                    {openStatusSubmenu === inv.id && createPortal(
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ position: "fixed", top: `${submenuPos.top}px`, right: `${submenuPos.right}px`, background: "#ffffff", border: "1px solid #E2E8F0", borderRadius: "12px", boxShadow: "0 8px 24px rgba(0,0,0,0.2)", zIndex: 1000001, minWidth: "185px", overflow: "hidden" }}
                      >
                        <div style={{ padding: "8px 14px 6px", borderBottom: "1px solid #f0f0f0" }}>
                          <span style={{ fontSize: "11px", fontWeight: "700", color: "#a0aec0", textTransform: "uppercase", letterSpacing: "0.6px" }}>Set Status</span>
                        </div>
                        {SELECTABLE_STATUSES.map(status => (
                          <button
                            key={status}
                            onClick={() => handleStatusUpdate(inv.id, status)}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "#f7f8fa")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                            style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "9px 14px", background: "transparent", border: "none", cursor: "pointer", color: "#4a5568", fontSize: "13px", textAlign: "left" }}
                          >
                            <span style={{ display: "inline-block", width: "9px", height: "9px", borderRadius: "50%", background: STATUS_CONFIG[status].bg, flexShrink: 0 }}></span>
                            {inv.effectiveStatus === status
                              ? <strong style={{ color: STATUS_CONFIG[status].bg }}>{status}</strong>
                              : status}
                          </button>
                        ))}
                      </div>,
                      document.body
                    )}
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="11" style={{ textAlign: "center", padding: "40px", color: "#718096" }}>No interviews found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Accept Confirmation Modal */}
      {confirmAccept.visible && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}
          onClick={() => setConfirmAccept({ visible: false, interview: null })}>
          <div style={{ background: "#fff", borderRadius: "18px", padding: "36px 32px", width: "100%", maxWidth: "420px", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "linear-gradient(135deg, #38a169, #21b11d)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: "0 4px 16px rgba(33,177,29,0.3)" }}>
              <i className="fas fa-check" style={{ color: "white", fontSize: "26px" }}></i>
            </div>
            <h3 style={{ margin: "0 0 8px", fontSize: "20px", fontWeight: "700", color: "#1a202c" }}>
              {confirmAccept.interview?.sourceType === "job" ? "Move to Hired Employees?" : "Move to Onboarded Trainees?"}
            </h3>
            <p style={{ margin: "0 0 6px", fontSize: "15px", color: "#4a5568", lineHeight: "1.6" }}>
              You are about to accept <strong style={{ color: "#1a202c" }}>{confirmAccept.interview?.name}</strong>
            </p>
            <p style={{ margin: "0 0 28px", fontSize: "13px", color: "#718096", lineHeight: "1.6" }}>
              Interview Rating: <strong>{confirmAccept.interview?.rating ? `${confirmAccept.interview.rating}/10` : "Not set"}</strong>
              <br />This will create a profile card in{" "}
              <strong>{confirmAccept.interview?.sourceType === "job" ? "Hired Employees" : "Onboarded Trainees"}</strong>{" "}
              and remove this interview record.
            </p>
            <div style={{ display: "flex", gap: "12px" }}>
              <button onClick={() => setConfirmAccept({ visible: false, interview: null })} style={{ flex: 1, padding: "12px", border: "1.5px solid #e2e8f0", borderRadius: "10px", cursor: "pointer", background: "#fff", color: "#718096", fontWeight: "600", fontSize: "14px" }}>Cancel</button>
              <button onClick={handleConfirmAccept} style={{ flex: 1, padding: "12px", border: "none", borderRadius: "10px", cursor: "pointer", background: "linear-gradient(135deg, #38a169, #21b11d)", color: "#fff", fontWeight: "700", fontSize: "14px" }}>Confirm Accept</button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Confirmation Modal */}
      {confirmReject.visible && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}
          onClick={() => setConfirmReject({ visible: false, interview: null })}>
          <div style={{ background: "#fff", borderRadius: "18px", padding: "36px 32px", width: "100%", maxWidth: "420px", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "linear-gradient(135deg, #e53e3e, #c53030)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: "0 4px 16px rgba(229,62,62,0.3)" }}>
              <i className="fas fa-times" style={{ color: "white", fontSize: "26px" }}></i>
            </div>
            <h3 style={{ margin: "0 0 8px", fontSize: "20px", fontWeight: "700", color: "#1a202c" }}>Reject Candidate?</h3>
            <p style={{ margin: "0 0 6px", fontSize: "15px", color: "#4a5568", lineHeight: "1.6" }}>
              You are about to reject <strong style={{ color: "#1a202c" }}>{confirmReject.interview?.name}</strong>
            </p>
            <p style={{ margin: "0 0 28px", fontSize: "13px", color: "#718096", lineHeight: "1.6" }}>
              This will move them back to the <strong>Rejected</strong> tab in applications and remove this interview record.
            </p>
            <div
              onClick={() => setSendRejectNotify(!sendRejectNotify)}
              style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "20px", padding: "12px 16px", border: "1.5px solid " + (sendRejectNotify ? "#e53e3e" : "#e2e8f0"), borderRadius: "12px", backgroundColor: sendRejectNotify ? "#fff5f5" : "#f8fafc", cursor: "pointer", textAlign: "left", transition: "all 0.2s ease" }}
            >
              <input type="checkbox" checked={sendRejectNotify} onChange={(e) => { e.stopPropagation(); setSendRejectNotify(e.target.checked); }} style={{ cursor: "pointer", marginTop: "4px" }} />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: "14px", fontWeight: "700", color: "#2d3748", cursor: "pointer" }}>Send Rejection Email</span>
                <span style={{ fontSize: "12px", color: "#718096", marginTop: "2px", lineHeight: "1.4" }}>Send a polite rejection notification to <strong>{confirmReject.interview?.email || "candidate"}</strong>.</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <button onClick={() => setConfirmReject({ visible: false, interview: null })} style={{ flex: 1, padding: "12px", border: "1.5px solid #e2e8f0", borderRadius: "10px", cursor: "pointer", background: "#fff", color: "#718096", fontWeight: "600", fontSize: "14px" }}>Cancel</button>
              <button onClick={handleConfirmReject} style={{ flex: 1, padding: "12px", border: "none", borderRadius: "10px", cursor: "pointer", background: "linear-gradient(135deg, #e53e3e, #c53030)", color: "#fff", fontWeight: "700", fontSize: "14px" }}>Confirm Reject</button>
            </div>
          </div>
        </div>
      )}

      {/* Block Confirmation Modal */}
      {confirmBlock.visible && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}
          onClick={() => setConfirmBlock({ visible: false, interview: null })}>
          <div style={{ background: "#fff", borderRadius: "18px", padding: "36px 32px", width: "100%", maxWidth: "420px", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "linear-gradient(135deg, #718096, #4a5568)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: "0 4px 16px rgba(113,128,150,0.3)" }}>
              <i className="fas fa-ban" style={{ color: "white", fontSize: "26px" }}></i>
            </div>
            <h3 style={{ margin: "0 0 8px", fontSize: "20px", fontWeight: "700", color: "#1a202c" }}>Block Candidate?</h3>
            <p style={{ margin: "0 0 6px", fontSize: "15px", color: "#4a5568", lineHeight: "1.6" }}>
              You are about to block <strong style={{ color: "#1a202c" }}>{confirmBlock.interview?.name}</strong>
            </p>
            <p style={{ margin: "0 0 28px", fontSize: "13px", color: "#718096", lineHeight: "1.6" }}>
              This will move them to the <strong>Blocked</strong> tab in applications and remove this interview record.
            </p>
            <div style={{ display: "flex", gap: "12px" }}>
              <button onClick={() => setConfirmBlock({ visible: false, interview: null })} style={{ flex: 1, padding: "12px", border: "1.5px solid #e2e8f0", borderRadius: "10px", cursor: "pointer", background: "#fff", color: "#718096", fontWeight: "600", fontSize: "14px" }}>Cancel</button>
              <button onClick={handleConfirmBlock} style={{ flex: 1, padding: "12px", border: "none", borderRadius: "10px", cursor: "pointer", background: "linear-gradient(135deg, #718096, #4a5568)", color: "#fff", fontWeight: "700", fontSize: "14px" }}>Confirm Block</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Accept Confirmation Modal */}
      {confirmBulkAccept && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}
          onClick={() => setConfirmBulkAccept(false)}>
          <div style={{ background: "#fff", borderRadius: "18px", padding: "36px 32px", width: "100%", maxWidth: "460px", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "linear-gradient(135deg, #38a169, #21b11d)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: "0 4px 16px rgba(33,177,29,0.3)" }}>
              <i className="fas fa-check" style={{ color: "white", fontSize: "26px" }}></i>
            </div>
            <h3 style={{ margin: "0 0 8px", fontSize: "20px", fontWeight: "700", color: "#1a202c" }}>Bulk Accept {selectedIds.size} Candidate{selectedIds.size !== 1 ? "s" : ""}?</h3>
            <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#718096", lineHeight: "1.6" }}>
              Each candidate will be moved to <strong>Hired Employees</strong> or <strong>Onboarded Trainees</strong> based on their application type, and their interview record will be removed.
            </p>
            <div style={{ maxHeight: "160px", overflowY: "auto", marginBottom: "24px", textAlign: "left", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "10px 14px" }}>
              {currentInterviews.filter(inv => selectedIds.has(inv.id)).map(inv => (
                <div key={inv.id} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid #f0f0f0", fontSize: "13px", color: "#2d3748" }}>
                  <span><strong>{inv.name}</strong></span>
                  <span style={{ color: inv.sourceType === "job" ? "#2b6cb0" : "#2f855a", fontWeight: "600", fontSize: "12px" }}>{inv.sourceType === "job" ? "→ Hired" : "→ Onboarded"}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <button onClick={() => setConfirmBulkAccept(false)} style={{ flex: 1, padding: "12px", border: "1.5px solid #e2e8f0", borderRadius: "10px", cursor: "pointer", background: "#fff", color: "#718096", fontWeight: "600", fontSize: "14px" }}>Cancel</button>
              <button onClick={handleConfirmBulkAccept} style={{ flex: 1, padding: "12px", border: "none", borderRadius: "10px", cursor: "pointer", background: "linear-gradient(135deg, #38a169, #21b11d)", color: "#fff", fontWeight: "700", fontSize: "14px" }}>Confirm Accept All</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Reject Confirmation Modal */}
      {confirmBulkReject && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}
          onClick={() => setConfirmBulkReject(false)}>
          <div style={{ background: "#fff", borderRadius: "18px", padding: "36px 32px", width: "100%", maxWidth: "460px", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "linear-gradient(135deg, #e53e3e, #c53030)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: "0 4px 16px rgba(229,62,62,0.3)" }}>
              <i className="fas fa-times" style={{ color: "white", fontSize: "26px" }}></i>
            </div>
            <h3 style={{ margin: "0 0 8px", fontSize: "20px", fontWeight: "700", color: "#1a202c" }}>Bulk Reject {selectedIds.size} Candidate{selectedIds.size !== 1 ? "s" : ""}?</h3>
            <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#718096", lineHeight: "1.6" }}>
              All selected candidates will be moved to the <strong>Rejected</strong> tab in applications and their interview records will be removed.
            </p>
            <div style={{ maxHeight: "140px", overflowY: "auto", marginBottom: "16px", textAlign: "left", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "10px 14px" }}>
              {currentInterviews.filter(inv => selectedIds.has(inv.id)).map(inv => (
                <div key={inv.id} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid #f0f0f0", fontSize: "13px", color: "#2d3748" }}>
                  <span><strong>{inv.name}</strong></span>
                  <span style={{ color: "#718096", fontSize: "12px" }}>{inv.email || "No email"}</span>
                </div>
              ))}
            </div>
            <div
              onClick={() => setSendBulkRejectNotify(!sendBulkRejectNotify)}
              style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "20px", padding: "12px 16px", border: "1.5px solid " + (sendBulkRejectNotify ? "#e53e3e" : "#e2e8f0"), borderRadius: "12px", backgroundColor: sendBulkRejectNotify ? "#fff5f5" : "#f8fafc", cursor: "pointer", textAlign: "left", transition: "all 0.2s ease" }}
            >
              <input type="checkbox" checked={sendBulkRejectNotify} onChange={(e) => { e.stopPropagation(); setSendBulkRejectNotify(e.target.checked); }} style={{ cursor: "pointer", marginTop: "4px" }} />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: "14px", fontWeight: "700", color: "#2d3748" }}>Send Rejection Emails</span>
                <span style={{ fontSize: "12px", color: "#718096", marginTop: "2px", lineHeight: "1.4" }}>Send a polite rejection notification to each candidate who has an email address.</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <button onClick={() => setConfirmBulkReject(false)} style={{ flex: 1, padding: "12px", border: "1.5px solid #e2e8f0", borderRadius: "10px", cursor: "pointer", background: "#fff", color: "#718096", fontWeight: "600", fontSize: "14px" }}>Cancel</button>
              <button onClick={handleConfirmBulkReject} style={{ flex: 1, padding: "12px", border: "none", borderRadius: "10px", cursor: "pointer", background: "linear-gradient(135deg, #e53e3e, #c53030)", color: "#fff", fontWeight: "700", fontSize: "14px" }}>Confirm Reject All</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Block Confirmation Modal */}
      {confirmBulkBlock && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}
          onClick={() => setConfirmBulkBlock(false)}>
          <div style={{ background: "#fff", borderRadius: "18px", padding: "36px 32px", width: "100%", maxWidth: "460px", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "linear-gradient(135deg, #718096, #4a5568)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: "0 4px 16px rgba(113,128,150,0.3)" }}>
              <i className="fas fa-ban" style={{ color: "white", fontSize: "26px" }}></i>
            </div>
            <h3 style={{ margin: "0 0 8px", fontSize: "20px", fontWeight: "700", color: "#1a202c" }}>Bulk Block {selectedIds.size} Candidate{selectedIds.size !== 1 ? "s" : ""}?</h3>
            <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#718096", lineHeight: "1.6" }}>
              All selected candidates will be moved to the <strong>Blocked</strong> tab in applications and their interview records will be removed.
            </p>
            <div style={{ maxHeight: "160px", overflowY: "auto", marginBottom: "24px", textAlign: "left", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "10px 14px" }}>
              {currentInterviews.filter(inv => selectedIds.has(inv.id)).map(inv => (
                <div key={inv.id} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid #f0f0f0", fontSize: "13px", color: "#2d3748" }}>
                  <span><strong>{inv.name}</strong></span>
                  <span style={{ color: "#718096", fontSize: "12px" }}>{inv.email || "No email"}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <button onClick={() => setConfirmBulkBlock(false)} style={{ flex: 1, padding: "12px", border: "1.5px solid #e2e8f0", borderRadius: "10px", cursor: "pointer", background: "#fff", color: "#718096", fontWeight: "600", fontSize: "14px" }}>Cancel</button>
              <button onClick={handleConfirmBulkBlock} style={{ flex: 1, padding: "12px", border: "none", borderRadius: "10px", cursor: "pointer", background: "linear-gradient(135deg, #718096, #4a5568)", color: "#fff", fontWeight: "700", fontSize: "14px" }}>Confirm Block All</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Details Modal */}
      {editModal.visible && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}>
          <div style={{ background: "white", padding: "30px", borderRadius: "16px", width: "450px", maxWidth: "90%", boxShadow: "0 20px 40px rgba(0,0,0,0.2)" }}>
            <h3 style={{ margin: "0 0 20px", fontSize: "20px", color: "#2d3748" }}>Interview Details</h3>
            <form onSubmit={handleSaveDetails}>
              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px", fontSize: "14px", color: "#4a5568", fontWeight: "600" }}>Interview Date</label>
                <input type="date" value={editModal.interview.interviewDate || ""} onChange={(e) => setEditModal({ ...editModal, interview: { ...editModal.interview, interviewDate: e.target.value } })} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e0", borderRadius: "8px" }} />
              </div>
              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px", fontSize: "14px", color: "#4a5568", fontWeight: "600" }}>Time Slot</label>
                <input type="text" value={editModal.interview.timeSlot || ""} onChange={(e) => setEditModal({ ...editModal, interview: { ...editModal.interview, timeSlot: e.target.value } })} placeholder="e.g., 10:00 AM - 11:00 AM" style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e0", borderRadius: "8px" }} />
              </div>
              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px", fontSize: "14px", color: "#4a5568", fontWeight: "600" }}>Assigned Interviewer</label>
                <input type="text" value={editModal.interview.assignedInterviewer || ""} onChange={(e) => setEditModal({ ...editModal, interview: { ...editModal.interview, assignedInterviewer: e.target.value } })} placeholder="Name of Interviewer" style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e0", borderRadius: "8px" }} />
              </div>
              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px", fontSize: "14px", color: "#4a5568", fontWeight: "600" }}>Overall Rating (0-10)</label>
                <input type="number" min="0" max="10" value={editModal.interview.rating || ""} onChange={(e) => setEditModal({ ...editModal, interview: { ...editModal.interview, rating: e.target.value } })} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e0", borderRadius: "8px" }} />
              </div>
              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "5px", fontSize: "14px", color: "#4a5568", fontWeight: "600" }}>Interview Notes</label>
                <textarea rows="4" value={editModal.interview.notes || ""} onChange={(e) => setEditModal({ ...editModal, interview: { ...editModal.interview, notes: e.target.value } })} placeholder="Add ongoing notes..." style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e0", borderRadius: "8px", resize: "vertical" }}></textarea>
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <button type="button" onClick={() => setEditModal({ visible: false, interview: null })} style={{ flex: 1, padding: "12px", background: "transparent", color: "#4a5568", border: "1px solid #cbd5e0", borderRadius: "8px", cursor: "pointer", fontWeight: "600" }}>Cancel</button>
                <button type="submit" style={{ flex: 1, padding: "12px", background: "#FF8C00", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600" }}>Save Details</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewManagement;
